/**
 * WALLET HOOK - API Methods & React Query Hooks Summary
 *
 * ===========================================
 * API METHODS (Internal Functions):
 * ===========================================
 * • createEurWallet()         - POST /wallets/create - Create EUR wallet for new users
 * • createWallets()           - POST /wallets/create - Create initial wallets (deprecated)
 * • requestWallet()           - POST /wallets/request - Request additional wallet (USD or GBP)
 * • setPrimaryWallet()        - PUT /wallets/{walletId}/primary - Set a wallet as primary
 * • setWalletVisibility()     - PUT /wallets/{walletId}/visibility - Hide or unhide a wallet
 * • getWalletBalance()        - GET /wallets/balance - Check wallet balance by currency
 * • initiateTopUp()           - POST /wallets/topup - Initiate wallet top-up (card/bank)
 * • confirmTopUp()            - POST /wallets/topup/confirm - Confirm top-up after 3D Secure
 * • initiateWithdraw()        - POST /wallets/withdraw - Initiate wallet withdrawal
 * • initiateTeamTransfer()    - POST /transactions/team-transfer - Initiate team transfer to team member
 * • convert()         - POST /wallets/convert - Convert currency between user's own wallets
 * • getWallets()              - GET /wallets - Fetch all user wallets
 * • getWalletTransferTypes()  - GET /wallets/transfer-types - Get supported transfer types
 * • checkDedicatedIbanAvailable() - GET /wallet-iban/{entityId}/dedicated-available - Check if dedicated IBAN is available
 * • requestDedicatedIban()    - POST /wallet-iban/{walletId}/request - Request dedicated IBAN for wallet
 * • getDedicatedIbanStatus()  - GET /wallet-iban/{entityId} - Get dedicated IBAN status for entity
 * • getWalletIbanKybStatus()  - GET /wallet-iban/{walletId}/kyb-status - Get dedicated IBAN KYB status
 * • startWalletIbanKyb()      - POST /wallet-iban/{walletId}/kyb/start - Start or resume KYB journey
 *
 * ===========================================
 * REACT QUERY HOOKS (Exported):
 * ===========================================
 * • useCreateEurWallet()                    - Mutation: Create EUR wallet for new users
 * • useCreateWallets()                      - Mutation: Create initial wallets (deprecated)
 * • useRequestWallet()                      - Mutation: Request additional wallet
 * • useSetPrimaryWallet()                   - Mutation: Set a wallet as primary
 * • useWalletBalance(currencyId, enabled?)  - Query: Get wallet balance by currency
 * • useTransferTypes()                      - Query: Get supported transfer types
 * • useInitiateTopUp()                      - Mutation: Start wallet top-up
 * • useConfirmTopUp()                       - Mutation: Confirm top-up after 3D Secure
 * • useInitiateWithdraw()                   - Mutation: Start wallet withdrawal
 * • useInitiateTeamTransfer()               - Mutation: Start team transfer to team member
 * • useConvert()                    - Mutation: Convert currency between user's own wallets
 * • useWallets(enabled?)                    - Query: Get all user wallets
 * • useWalletByCurrency(currencyId, enabled?) - Query: Get specific wallet by currency
 * • useDedicatedIbanAvailable(entityId, enabled?) - Query: Check if dedicated IBAN is available
 * • useRequestDedicatedIban()               - Mutation: Request dedicated IBAN for wallet
 * • useDedicatedIbanStatus(entityId, enabled?) - Query: Get dedicated IBAN status for entity
 * • useRequestIbanKyb()                     - Mutation: Start KYB and return Tally form URL
 *
 * ===========================================
 * KEY FEATURES:
 * ===========================================
 * • Authentication via Bearer token (store + cookie fallback)
 * • Support for multiple providers: 'card', 'bank'
 * • 10-minute stale time for balance & wallet data
 * • Automatic query invalidation on wallet operations
 * • Support for transfer types: PAYROLL, EXPENSE, INVOICE, OTHER
 * • Stripe integration for card payments with 3D Secure
 * • Currency-based wallet filtering and selection
 * ===========================================
 */

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { ApiError } from '@workspace/ui/types';
import { fetchWalletIbanEntities, type WalletIbanEntity } from '@/fetchers/wallet';
import fetchAPI from '@/lib/fetch-api';
import type { Wallet } from '@/types/wallet';

// ==================== INTERFACES ====================

interface Response {
  message: string;
  status: number;
  type: string;
}

interface CreateWalletsResponse extends Response {
  success: boolean;
}

interface RequestWalletRequest {
  currency_id: number;
  wallet_type?: 'default' | 'pre_payment';
}

interface RequestWalletResponse extends Response {
  success?: boolean;
}

interface SetPrimaryWalletResponse extends Response {
  id: number;
  is_primary: boolean;
  is_visible: boolean;
}

interface BankDetail {
  id: number;
  is_external: number;
  iban: string;
  primary: number;
  address: string | null;
  name: string | null;
  account_number: string;
  swift: string | null;
  holder: string;
  holder_city: string | null;
  holder_address: string | null;
  holder_zip_code: string | null;
  phone: string | null;
  identifying_number: string | null;
  aba_routing_number: string | null;
  currency_id: number;
}

interface WalletBalance {
  id: number;
  user_id: number;
  available_balance: string;
  current_balance: string;
  reserved_balance: string;
  reference_number: string;
  currency_id: number;
  bank_details: BankDetail[];
  pending_balance_rewards_total: string;
  balance_rewards_enabled: boolean;
}

/**
 * Transfer types returned from API as { "ENUM_KEY": "kebab-case-value", ... }
 * Keys are enum names (e.g., "PAYROLL"), values are kebab-case strings for API calls (e.g., "payroll")
 * Values include: payroll, expense, invoice, bank-withdrawal (was bank-withdraw), team-transfer,
 * currency-conversion, payoneer, refund (was internal-refund), eps-card-top-up, rewards (was balance-reward),
 * wallet-top-up (was card-top-up), bank-transfer (was bank-top-up), subscription, other
 */
type TransferType = Record<string, string>;

interface TopUpRequest {
  provider: 'card' | 'bank';
  amount: number;
  currency_id: number;
  recipient_user_id: string;
  card_id?: number;
  wallet_id: number;
  reason?: string;
}

interface TopUpData {
  payment_intent_id: string;
  payment_intent_client_secret?: string;
}

interface ConfirmTopUpResponse extends Response {
  success?: boolean;
}

interface WithdrawRequest {
  wallet_id: number;
  provider: 'bank' | 'card';
  amount: number;
  currency_id: number;
  bank_id?: number;
  /** Required for MKD local bank withdrawals — see POST /wallets/withdraw `contract`. */
  contract?: {
    document_id: number;
  };
}

interface WithdrawData {
  id: number;
  status: string;
  amount: number;
  provider: string;
  wallet_id: number;
  currency_id: number;
  created_at: string;
}

interface TeamTransferRequest {
  recipient_user_id: number;
  recipient_name: string;
  from_currency_id: number;
  to_currency_id: number;
  amount: number;
  automatic_withdraw: boolean;
  bank_account_id?: number;
  transfer_description: string;
}

interface TeamTransferData {
  transfer_transaction_id: number;
  withdrawal_transaction_id: number | null;
}

interface ConvertRequest {
  from_currency_id: number;
  to_currency_id: number;
  amount: number;
}

interface ConvertData {
  transaction_id: number;
}

/** Unwrapped data returned by checkDedicatedIbanAvailable (response.data) */
export interface DedicatedIbanAvailableData {
  available: boolean;
}

interface RequestDedicatedIbanResponse extends Response {
  success?: boolean;
}

interface IbanDetailsData {
  wallet_id: number;
  status: 'new' | 'pending' | 'active' | 'declined';
  holder: string;
  iban: string;
  swift_bic: string;
  is_dedicated: boolean;
  requested_at: string;
  activated_at: string | null;
}

interface WalletIbanKybStatusData {
  status: 'not_started' | 'in_progress' | 'submitted';
  form_url: string | null;
  reference: string | null;
}

interface WalletIbanKybStartData {
  status: 'in_progress';
  form_url: string;
  reference: string;
}

export interface RequestIbanKybResult {
  status: 'not_started' | 'in_progress' | 'submitted';
  formUrl: string | null;
}

// ==================== API FUNCTIONS ====================

/**
 * Creates initial EUR wallet for the authenticated user (new users only)
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<CreateWalletsResponse>} The wallet creation response
 */
export const createEurWallet = async (): Promise<CreateWalletsResponse> => {
  const response = await fetchAPI<CreateWalletsResponse>('/wallets/create', {
    method: 'POST',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to create EUR wallet');
  }

  return response.data!;
};

/**
 * Requests an additional wallet in USD or GBP currency
 * @param {RequestWalletRequest} requestData - The wallet request data
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<RequestWalletResponse>} The wallet request response
 */
const requestWallet = async (requestData: RequestWalletRequest): Promise<RequestWalletResponse> => {
  const response = await fetchAPI<RequestWalletResponse>('/wallets/request', {
    method: 'POST',
    body: JSON.stringify(requestData),
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to request wallet');
  }

  return response.data!;
};

/**
 * Marks a wallet as the owner's primary wallet
 * @param {number} walletId - The ID of the wallet to promote
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<SetPrimaryWalletResponse>} The updated primary wallet payload
 */
const setPrimaryWallet = async (walletId: number): Promise<SetPrimaryWalletResponse> => {
  const response = await fetchAPI<SetPrimaryWalletResponse>(`/wallets/${walletId}/primary`, {
    method: 'PUT',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to set primary wallet');
  }

  return response.data!;
};

/**
 * Hides or unhides one of the owner's wallets
 * @throws {Error} With the backend message when hiding is rejected (balance, primary, last visible)
 */
const setWalletVisibility = async ({
  walletId,
  isVisible,
}: {
  walletId: number;
  isVisible: boolean;
}): Promise<Wallet> => {
  const response = await fetchAPI<Wallet>(`/wallets/${walletId}/visibility`, {
    method: 'PUT',
    body: JSON.stringify({ is_visible: isVisible }),
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to update wallet visibility');
  }

  return response.data!;
};

/**
 * Checks wallet balance for the authenticated user
 * @param {number} currencyId - The currency ID to fetch the wallet balance for
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<WalletBalance>} The wallet balance data
 */
export const getWalletBalance = async (currencyId: number): Promise<WalletBalance> => {
  const response = await fetchAPI<WalletBalance>(`/wallets/balance?currency_id=${currencyId}`, {
    method: 'GET',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch wallet balance');
  }

  return response.data!;
};

/**
 * Initiates a wallet top-up using saved card or bank transfer
 * @param {TopUpRequest} topUpData - The top-up request data
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<TopUpData>} The top-up response data
 */
const initiateTopUp = async (topUpData: TopUpRequest): Promise<TopUpData> => {
  const response = await fetchAPI<TopUpData>('/wallets/topup', {
    method: 'POST',
    body: JSON.stringify(topUpData),
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to initiate top-up');
  }

  return response.data!;
};

const PENDING_TOP_UP_NOT_FOUND_MESSAGE = 'pending top-up not found';

function isCompletedTopUpConfirmError(error: Error): error is ApiError {
  if (!(error instanceof ApiError) || error.status !== 404) return false;

  return [error.message, error.error].some(
    (value) => value?.trim().toLowerCase() === PENDING_TOP_UP_NOT_FOUND_MESSAGE
  );
}

/**
 * Confirms wallet top-up after 3-D Secure authentication.
 * A 404 "Pending top-up not found" means the top-up already completed during initiate,
 * so callers follow the existing success path instead of showing an error.
 * @param {string} paymentIntentId - The payment intent ID to confirm
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<ConfirmTopUpResponse>} The confirmation response
 */
const confirmTopUp = async (paymentIntentId: string): Promise<ConfirmTopUpResponse> => {
  try {
    const response = await fetchAPI<ConfirmTopUpResponse>('/wallets/topup/confirm', {
      method: 'POST',
      body: JSON.stringify({ payment_intent_id: paymentIntentId }),
    });

    if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
      throw new Error(response.error || response.message || 'Failed to confirm top-up');
    }

    return response.data!;
  } catch (error) {
    if (!(error instanceof Error) || !isCompletedTopUpConfirmError(error)) throw error;

    return {
      message: error.message,
      status: error.status ?? 404,
      type: 'success',
      success: true,
    };
  }
};

/**
 * Initiates a withdrawal from the user's wallet
 * @param {WithdrawRequest} withdrawData - The withdrawal request data
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<WithdrawData>} The withdrawal response data
 */
const initiateWithdraw = async (withdrawData: WithdrawRequest): Promise<WithdrawData> => {
  const response = await fetchAPI<WithdrawData>('/wallets/withdraw', {
    method: 'POST',
    body: JSON.stringify(withdrawData),
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to initiate withdrawal');
  }

  return response.data!;
};

/**
 * Initiates a team transfer to a team member with optional automatic withdrawal
 * @param {TeamTransferRequest} transferData - The team transfer request data
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<TeamTransferData>} The team transfer response data
 */
const initiateTeamTransfer = async (
  transferData: TeamTransferRequest
): Promise<TeamTransferData> => {
  const response = await fetchAPI<TeamTransferData>('/transactions/team-transfer', {
    method: 'POST',
    body: JSON.stringify(transferData),
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to initiate team transfer');
  }

  return response.data!;
};

/**
 * Converts currency between the authenticated user's own wallets
 * @param {ConvertRequest} convertData - The currency conversion request data
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<ConvertData>} The currency conversion response data
 */
const convert = async (convertData: ConvertRequest): Promise<ConvertData> => {
  const response = await fetchAPI<ConvertData>('/wallets/convert', {
    method: 'POST',
    body: JSON.stringify(convertData),
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to convert currency');
  }

  return response.data!;
};

interface GetWalletsOptions {
  /** Filter wallets by user ID (passed to API) */
  userId?: number;
  /** Filter wallets by wallet type */
  walletType?: 'default' | 'pre_payment' | 'organisation' | 'entity' | 'salary';
}

/**
 * Fetches all wallets for the authenticated user
 * @param {GetWalletsOptions} options - Optional filtering parameters
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<Wallet[]>} Array of wallet records
 */
export const getWallets = async (options?: GetWalletsOptions): Promise<Wallet[]> => {
  const params = new URLSearchParams();

  if (options?.userId) {
    params.append('user_id', options.userId.toString());
  }
  if (options?.walletType) {
    params.append('wallet_type', options.walletType);
  }

  const queryString = params.toString();
  const url = queryString ? `/wallets?${queryString}` : '/wallets';

  const response = await fetchAPI<Wallet[]>(url, {
    method: 'GET',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch wallets');
  }

  return response.data!;
};

/**
 * Fetches supported wallet transfer types
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<TransferType>} The transfer types data
 */
const getWalletTransferTypes = async (): Promise<TransferType> => {
  const response = await fetchAPI<TransferType>('/wallets/transfer-types', {
    method: 'GET',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch transfer types');
  }

  return response.data!;
};

/**
 * Checks if a dedicated IBAN is available for the entity
 * @param {number} entityId - The entity/organization ID
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<DedicatedIbanAvailableResponse>} The availability response
 */
const checkDedicatedIbanAvailable = async (
  entityId: number
): Promise<DedicatedIbanAvailableData> => {
  const response = await fetchAPI<DedicatedIbanAvailableData>(
    `/wallet-iban/${entityId}/dedicated-available`,
    {
      method: 'GET',
    }
  );

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(
      response.error || response.message || 'Failed to check dedicated IBAN availability'
    );
  }

  return response.data!;
};

/**
 * Requests a dedicated IBAN for a wallet
 * @param {number} walletId - The wallet ID
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<RequestDedicatedIbanResponse>} The request response
 */
const requestDedicatedIban = async (walletId: number): Promise<RequestDedicatedIbanResponse> => {
  const response = await fetchAPI<RequestDedicatedIbanResponse>(
    `/wallet-iban/${walletId}/request`,
    {
      method: 'POST',
    }
  );

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to request dedicated IBAN');
  }

  return response.data!;
};

/**
 * Gets the dedicated IBAN status for an entity
 * @param {number} entityId - The entity/organization ID
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<IbanDetailsResponse>} The status response
 */
const getDedicatedIbanStatus = async (entityId: number): Promise<IbanDetailsData> => {
  const response = await fetchAPI<IbanDetailsData>(`/wallet-iban/${entityId}`, {
    method: 'GET',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch dedicated IBAN status');
  }

  return response.data!;
};

/**
 * Gets dedicated IBAN KYB status for a wallet
 * @param {number} walletId - The wallet ID
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<WalletIbanKybStatusData>} The KYB status and optional form URL
 */
const getWalletIbanKybStatus = async (walletId: number): Promise<WalletIbanKybStatusData> => {
  const response = await fetchAPI<WalletIbanKybStatusData>(`/wallet-iban/${walletId}/kyb-status`, {
    method: 'GET',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch IBAN KYB status');
  }

  return response.data!;
};

/**
 * Starts or resumes the dedicated IBAN KYB journey for a wallet
 * @param {number} walletId - The wallet ID
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<WalletIbanKybStartData>} The in-progress KYB form URL
 */
const startWalletIbanKyb = async (walletId: number): Promise<WalletIbanKybStartData> => {
  const response = await fetchAPI<WalletIbanKybStartData>(`/wallet-iban/${walletId}/kyb/start`, {
    method: 'POST',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to start IBAN KYB');
  }

  return response.data!;
};

/**
 * Resolves the Tally form URL for a dedicated IBAN KYB request
 * @param {number} walletId - The wallet ID
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<RequestIbanKybResult>} KYB status and form URL when the form should open
 */
const requestIbanKyb = async (walletId: number): Promise<RequestIbanKybResult> => {
  const kybStatus = await getWalletIbanKybStatus(walletId);

  if (kybStatus.status === 'submitted') {
    return { status: kybStatus.status, formUrl: null };
  }

  if (kybStatus.form_url) {
    return { status: kybStatus.status, formUrl: kybStatus.form_url };
  }

  const startedKyb = await startWalletIbanKyb(walletId);
  return { status: startedKyb.status, formUrl: startedKyb.form_url };
};

// ==================== REACT QUERY HOOKS ====================

/**
 * Hook to create initial EUR wallet for new users
 * @returns {UseMutationResult} Mutation result for creating EUR wallet
 */
export function useCreateEurWallet(): UseMutationResult<CreateWalletsResponse, Error, void> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createEurWallet,
    onSuccess: () => {
      // Invalidate wallet-related queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['wallets'] });
      queryClient.invalidateQueries({ queryKey: ['wallet-balance'] });
      // Invalidate transactions as new wallet creation may affect transaction history
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

/**
 * Hook to fetch wallet balance for a specific currency
 * @param {number} currencyId - The currency ID to fetch balance for
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {UseQueryResult} Query result for wallet balance
 */
export function useWalletBalance(
  currencyId: number,
  enabled: boolean = true
): UseQueryResult<WalletBalance, Error> {
  return useQuery({
    queryKey: ['wallet-balance', currencyId],
    queryFn: () => getWalletBalance(currencyId),
    enabled: enabled && !!currencyId,
    staleTime: 600000, // 10 minutes
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnReconnect: false, // Prevent refetch on network reconnect
    refetchInterval: false, // Disable automatic refetching
  });
}

/**
 * Hook to fetch supported wallet transfer types
 * @returns {UseQueryResult} Query result for transfer types
 */
export function useTransferTypes(): UseQueryResult<TransferType, Error> {
  return useQuery({
    queryKey: ['wallet-transfer-types'],
    queryFn: getWalletTransferTypes,
    staleTime: 300000, // 5 minutes (transfer types rarely change)
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnReconnect: false, // Prevent refetch on network reconnect
    refetchInterval: false, // Disable automatic refetching
  });
}

/**
 * Hook to initiate wallet top-up
 * @returns {UseMutationResult} Mutation result for initiating top-up
 */
export function useInitiateTopUp(): UseMutationResult<TopUpData, Error, TopUpRequest> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: initiateTopUp,
    onSuccess: () => {
      // Invalidate wallet-related queries
      queryClient.invalidateQueries({ queryKey: ['wallet-balance'] });
      queryClient.invalidateQueries({ queryKey: ['wallets'] });
      // Invalidate transactions to show the new top-up transaction
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      // Allowance decreases after a successful top-up for demo-verified users
      queryClient.invalidateQueries({ queryKey: ['kyc-verification', 'demo-overage'] });
    },
  });
}

/**
 * Hook to confirm wallet top-up after 3-D Secure
 * @returns {UseMutationResult} Mutation result for confirming top-up
 */
export function useConfirmTopUp(): UseMutationResult<ConfirmTopUpResponse, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: confirmTopUp,
    onSuccess: () => {
      // Invalidate wallet-related queries
      queryClient.invalidateQueries({ queryKey: ['wallet-balance'] });
      queryClient.invalidateQueries({ queryKey: ['wallets'] });
      // Invalidate transactions to show the updated transaction status
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      // Allowance decreases after a successful top-up for demo-verified users
      queryClient.invalidateQueries({ queryKey: ['kyc-verification', 'demo-overage'] });
    },
  });
}

/**
 * Hook to initiate wallet withdrawal
 * @returns {UseMutationResult} Mutation result for initiating withdrawal
 */
export function useInitiateWithdraw(): UseMutationResult<WithdrawData, Error, WithdrawRequest> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: initiateWithdraw,
    onSuccess: () => {
      // Invalidate wallet-related queries
      queryClient.invalidateQueries({ queryKey: ['wallet-balance'] });
      queryClient.invalidateQueries({ queryKey: ['wallets'] });
      // Invalidate transactions to show the new withdrawal transaction
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

/**
 * Hook to initiate team transfer to a team member
 * @returns {UseMutationResult} Mutation result for initiating team transfer
 */
export function useInitiateTeamTransfer(): UseMutationResult<
  TeamTransferData,
  Error,
  TeamTransferRequest
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: initiateTeamTransfer,
    onSuccess: () => {
      // Invalidate wallet-related queries
      queryClient.invalidateQueries({ queryKey: ['wallet-balance'] });
      queryClient.invalidateQueries({ queryKey: ['wallets'] });
      // Invalidate transactions to show the new team transfer transaction
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

/**
 * Hook to convert currency between user's own wallets
 * @returns {UseMutationResult} Mutation result for converting currency
 */
export function useConvert(): UseMutationResult<ConvertData, Error, ConvertRequest> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: convert,
    onSuccess: () => {
      // Invalidate wallet-related queries
      queryClient.invalidateQueries({ queryKey: ['wallet-balance'] });
      queryClient.invalidateQueries({ queryKey: ['wallets'] });
      // Invalidate transactions to show the new conversion transaction
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

/**
 * Hook to fetch all wallets for the authenticated user
 * @param {boolean} enabled - Whether the query should be enabled
 * @param {GetWalletsOptions} options - Optional filtering parameters (userId, walletType)
 * @returns {UseQueryResult} Query result for wallets
 */
export function useWallets(
  enabled: boolean = true,
  options?: GetWalletsOptions
): UseQueryResult<Wallet[], Error> {
  return useQuery({
    queryKey: ['wallets', options],
    queryFn: () => getWallets(options),
    enabled,
    staleTime: 600000, // 10 minutes
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnReconnect: false, // Prevent refetch on network reconnect
    refetchInterval: false, // Disable automatic refetching
  });
}

/**
 * Hook to fetch a single wallet by currency ID and type using the cached result of useWallets.
 * Defaults to 'default' wallet type to prevent accidentally selecting pre_payment wallets
 * when both exist for the same currency.
 *
 * @param {number | string} currencyId - Currency ID
 * @param {boolean} enabled - Whether the selection should be enabled
 * @param {GetWalletsOptions} options - Optional filtering parameters (userId, walletType)
 * @param {string} walletType - Wallet type to filter for (defaults to 'default'); ignored if options.walletType is set
 */
export function useWalletByCurrency(
  currencyId: number | string,
  enabled: boolean = true,
  options?: GetWalletsOptions,
  walletType: 'default' | 'pre_payment' = 'default'
): UseQueryResult<Wallet | undefined, Error> {
  const numericId = typeof currencyId === 'string' ? Number(currencyId) : currencyId;
  // Only use options.walletType if it matches Wallet.type values; otherwise fall back to parameter
  const optionsWalletType = options?.walletType;
  const resolvedWalletType: 'default' | 'pre_payment' =
    optionsWalletType === 'default' || optionsWalletType === 'pre_payment'
      ? optionsWalletType
      : walletType;

  return useQuery({
    queryKey: ['wallets', options],
    queryFn: () => getWallets(options),
    select: (wallets) =>
      wallets.find(
        (wallet) => wallet.currency_id === numericId && wallet.type === resolvedWalletType
      ),
    enabled,
    staleTime: 600000, // 10 minutes
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnReconnect: false, // Prevent refetch on network reconnect
    refetchInterval: false, // Disable automatic refetching
  });
}

/**
 * Hook to fetch a single entity wallet by its ID (for Entity wallet tab add/send money flows).
 * Uses entity wallets from GET /wallets?wallet_type=entity and selects the matching wallet.
 * @param {number | undefined} walletId - Entity wallet ID
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {UseQueryResult} Query result for the wallet or undefined
 */
export function useWalletById(
  walletId: number | undefined,
  enabled: boolean = true,
  walletType: 'entity' | 'salary' = 'entity'
): UseQueryResult<Wallet | undefined, Error> {
  return useQuery({
    queryKey: ['wallets', { walletType }],
    queryFn: () => getWallets({ walletType }),
    select: (wallets) => wallets.find((wallet) => wallet.id === walletId),
    enabled: enabled && !!walletId,
    staleTime: 600000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
}

/**
 * Hook to request an additional wallet
 * @returns {UseMutationResult} Mutation result for requesting wallet
 */
export function useRequestWallet(): UseMutationResult<
  RequestWalletResponse,
  Error,
  RequestWalletRequest
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: requestWallet,
    onSuccess: () => {
      // Invalidate wallet-related queries
      queryClient.invalidateQueries({ queryKey: ['wallets'] });
      queryClient.invalidateQueries({ queryKey: ['wallet-balance'] });
      // Invalidate transactions as new wallet may affect transaction history
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

/**
 * Hook to set a wallet as the owner's primary wallet
 * @returns {UseMutationResult} Mutation result for setting the primary wallet
 */
export function useSetPrimaryWallet(): UseMutationResult<SetPrimaryWalletResponse, Error, number> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: setPrimaryWallet,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallets'] });
      queryClient.invalidateQueries({ queryKey: ['wallet-balance'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

/**
 * Hook to hide or unhide one of the owner's wallets
 * @returns {UseMutationResult} Mutation result for updating wallet visibility
 */
export function useSetWalletVisibility(): UseMutationResult<
  Wallet,
  Error,
  { walletId: number; isVisible: boolean }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: setWalletVisibility,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallets'] });
    },
  });
}

/**
 * Hook to check if dedicated IBAN is available for an entity
 * @param {number} entityId - The entity/organization ID
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {UseQueryResult} Query result for dedicated IBAN availability
 */
export function useDedicatedIbanAvailable(
  entityId: number | undefined,
  enabled: boolean = true
): UseQueryResult<DedicatedIbanAvailableData, Error> {
  return useQuery({
    queryKey: ['dedicated-iban-available', entityId],
    queryFn: () => checkDedicatedIbanAvailable(entityId!),
    enabled: enabled && !!entityId,
    staleTime: 600000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
}

/**
 * Hook to request a dedicated IBAN for a wallet
 * @returns {UseMutationResult} Mutation result for requesting dedicated IBAN
 */
export function useRequestDedicatedIban(): UseMutationResult<
  RequestDedicatedIbanResponse,
  Error,
  number
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: requestDedicatedIban,
    onSuccess: () => {
      // Invalidate dedicated IBAN availability query
      queryClient.invalidateQueries({ queryKey: ['dedicated-iban-available'] });
      // Invalidate dedicated IBAN status query
      queryClient.invalidateQueries({ queryKey: ['dedicated-iban-status'] });
      // Invalidate wallet-related queries as IBAN request may affect wallet data
      queryClient.invalidateQueries({ queryKey: ['wallets'] });
    },
  });
}

/**
 * Hook to get dedicated IBAN status for an entity
 * @param {number} entityId - The entity/organization ID
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {UseQueryResult} Query result for dedicated IBAN status
 */
export function useDedicatedIbanStatus(
  entityId: number | undefined,
  enabled: boolean = true
): UseQueryResult<IbanDetailsData, Error> {
  return useQuery({
    queryKey: ['dedicated-iban-status', entityId],
    queryFn: () => getDedicatedIbanStatus(entityId!),
    enabled: enabled && !!entityId,
    staleTime: 600000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
}

/**
 * Hook to start or resume dedicated IBAN KYB and return the Tally form URL
 * @returns {UseMutationResult} Mutation result with KYB status and optional form URL
 */
export function useRequestIbanKyb(): UseMutationResult<RequestIbanKybResult, Error, number> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: requestIbanKyb,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['dedicated-iban-status'] });
      queryClient.invalidateQueries({ queryKey: ['dedicated-iban-available'] });
    },
  });
}

/**
 * Fetches entities for the entity wallet dropdown (GET /wallet-iban/entities)
 * @param {boolean} enabled - Whether the query should run
 * @returns {UseQueryResult<WalletIbanEntity[], Error>} List of entities with id, wallet_id, name, status
 */
export function useWalletIbanEntities(
  enabled: boolean = true
): UseQueryResult<WalletIbanEntity[], Error> {
  return useQuery({
    queryKey: ['wallet-iban', 'entities'],
    queryFn: fetchWalletIbanEntities,
    enabled,
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
}
